import { useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Button, TextField } from "@/components/ui";
import { api, ApiError } from "@/lib/api";
import { useAppConfig } from "@/lib/queries";
import type { MeDTO, MfaChallengeDTO } from "@shared/types";
import { MfaStep } from "@/components/MfaStep";
import { LegalLinks } from "@/components/Legal";
import { PasskeySignIn } from "@/components/PasskeySignIn";
import { AuthLayout } from "./AuthLayout";
import { PairedKids } from "./PairedKids";

export function ParentLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [ticket, setTicket] = useState<string | null>(null);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from;
  const config = useAppConfig();

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<MeDTO | MfaChallengeDTO>("/auth/login", { email, password });
      if ("mfaRequired" in res) setTicket(res.ticket);
      else signedIn(res);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not sign in");
    } finally {
      setBusy(false);
    }
  }

  function signedIn(me: MeDTO) {
    qc.setQueryData(["me"], me);
    if (me.user.role === "kid") return navigate("/kid", { replace: true });
    navigate(from?.startsWith("/parent") || from?.startsWith("/connect") ? from : me.family.onboarded ? "/parent" : "/parent/setup", {
      replace: true,
    });
  }

  if (ticket) {
    return (
      <AuthLayout title="Two-step verification" subtitle="Open your authenticator app and enter the code for Meaningful Rewards.">
        <MfaStep
          ticket={ticket}
          onDone={signedIn}
          onRestart={() => {
            setTicket(null);
            setPassword("");
          }}
        />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Welcome back" subtitle="Sign in to your family account.">
      <PairedKids className="mb-6" />
      <PasskeySignIn primary className="mb-4" onDone={signedIn} />
      <form onSubmit={submit} className="space-y-4">
        <TextField label="Email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
        <TextField
          label="Password"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={error}
        />
        <Button type="submit" size="lg" block loading={busy}>
          Sign in
        </Button>
        {config.data?.passwordReset && (
          <p className="text-center text-sm">
            <Link to="/forgot-password" className="font-bold text-brand">
              Forgot your password?
            </Link>
          </p>
        )}
      </form>
      <div className="mt-6 space-y-2 text-center text-sm text-muted">
        <p>
          New here?{" "}
          <Link to="/signup" className="font-bold text-brand">
            Create a family
          </Link>
        </p>
        <p>
          Have an invite code?{" "}
          <Link to="/join" className="font-bold text-brand">
            Join as a co-parent
          </Link>
        </p>
        <p>
          Kid?{" "}
          <Link to="/kid-login" className="font-bold text-brand">
            Use your PIN
          </Link>
        </p>
      </div>
      <LegalLinks className="mt-8" />
    </AuthLayout>
  );
}
